"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Printer, Settings, Eye, Save, RotateCcw } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { numberToMoney } from "@/util/moneyConversion";
import { formatCep } from "@/util/formatters";
import type {
  ReceiptReportSettings,
  PrintReceiptProps,
} from "@/interfaces/receipt-report";

// Configurações padrão
const DEFAULT_SETTINGS: ReceiptReportSettings = {
  paperWidth: 80,
  fontSize: 14,
  showLogo: true,
  showCompanyInfo: true,
  showCustomerInfo: true,
  showCustomerDocument: false,
  showItemPrices: true,
  showItemQuantities: true,
  showSubtotals: true,
  showTags: false,
  showHeader: true,
  showFooter: true,
  showPaymentDetails: true,
  showDeliveryInfo: true,
  showOrderNumber: true,
  showDescription: true,
  compactMode: false,
  copies: 1,
  showAdditionals: true,
};

export function PrintReceipt({
  report,
  initialSettings = {},
  showControls = true,
  onPrint,
  configOnly = false,
  previewOnly = false,
}: PrintReceiptProps) {
  // Carregar configurações do localStorage ou usar padrão + iniciais
  const loadSettings = (): ReceiptReportSettings => {
    try {
      const savedSettings = localStorage.getItem("receiptReportSettings");
      if (savedSettings) {
        return {
          ...DEFAULT_SETTINGS,
          ...JSON.parse(savedSettings),
          ...initialSettings,
        };
      }
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
    }
    return { ...DEFAULT_SETTINGS, ...initialSettings };
  };

  const [settings, setSettings] = useState<ReceiptReportSettings>(loadSettings);
  const [activeTab, setActiveTab] = useState<string>(
    configOnly ? "settings" : previewOnly ? "preview" : "preview"
  );
  const printContentRef = useRef<HTMLDivElement>(null);

  // Salvar configurações quando mudarem
  useEffect(() => {
    try {
      localStorage.setItem("receiptReportSettings", JSON.stringify(settings));
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
    }
  }, [settings]);

  const handlePrint = () => {
    const printContent = printContentRef.current;
    if (!printContent) return;

    const originalContents = document.body.innerHTML;
    const printContents = printContent.innerHTML;

    document.body.innerHTML = `
      <style>
        @page { size: ${settings.paperWidth}mm auto; margin: 0; }
        body { margin: 0; padding: 10px; font-family: sans-serif; font-size: ${
          settings.fontSize
        }px; }
        .print-content { width: ${settings.paperWidth - 20}mm; }
        .no-print { display: none; }
        hr { border: none; border-top: 1px dashed #000; }
        .item-row { margin-bottom: ${settings.compactMode ? "2px" : "8px"}; }
        .text-xs { font-size: ${settings.fontSize - 2}px; }
        .text-sm { font-size: ${settings.fontSize}px; }
        .text-base { font-size: ${settings.fontSize + 2}px; }
        .text-lg { font-size: ${settings.fontSize + 4}px; }
        .text-xl { font-size: ${settings.fontSize + 6}px; }
        .text-2xl { font-size: ${settings.fontSize + 8}px; }
        .font-bold { font-weight: bold; }
        .text-center { text-align: center; }
        .mb-1 { margin-bottom: 0.25rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-4 { margin-bottom: 1rem; }
        .mt-2 { margin-top: 0.5rem; }
        .mt-4 { margin-top: 1rem; }
        .flex { display: flex; }
        .justify-between { justify-content: space-between; }
        .items-center { align-items: center; }
        .w-full { width: 100%; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 4px; }
        .border-b { border-bottom: 1px solid #eee; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .text-left { text-align: left; }
        .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
        .pt-1 { padding-top: 0.25rem; }
        .mt-1 { margin-top: 0.25rem; }
        .mt-3 { margin-top: 0.75rem; }
      </style>
      <div class="print-content">${printContents}</div>
    `;

    window.print();
    document.body.innerHTML = originalContents;

    // Chamar o callback onPrint se fornecido
    if (onPrint) {
      onPrint();
    }
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const saveSettings = () => {
    try {
      localStorage.setItem("receiptReportSettings", JSON.stringify(settings));
      alert("Configurações salvas com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      alert("Erro ao salvar configurações.");
    }
  };

  const updateSetting = <K extends keyof ReceiptReportSettings>(
    key: K,
    value: ReceiptReportSettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "";
    const d = new Date(date);
    return (
      d.toLocaleDateString("pt-BR") +
      " " +
      d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    );
  };

  // Renderizar apenas as configurações
  if (configOnly) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Label>Largura do papel (mm)</Label>
          <div className="flex items-center gap-4">
            <Slider
              value={[settings.paperWidth]}
              min={50}
              max={210}
              step={5}
              onValueChange={(value) => updateSetting("paperWidth", value[0])}
              className="flex-1"
            />
            <span className="w-12 text-right">{settings.paperWidth}mm</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Tamanho da fonte</Label>
          <div className="flex items-center gap-4">
            <Slider
              value={[settings.fontSize]}
              min={10}
              max={24}
              step={1}
              onValueChange={(value) => updateSetting("fontSize", value[0])}
              className="flex-1"
            />
            <span className="w-12 text-right">{settings.fontSize}px</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Número de cópias</Label>
          <div className="flex items-center gap-4">
            <Slider
              value={[settings.copies]}
              min={1}
              max={5}
              step={1}
              onValueChange={(value) => updateSetting("copies", value[0])}
              className="flex-1"
            />
            <span className="w-12 text-right">{settings.copies}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="showHeader">Mostrar cabeçalho</Label>
            <Switch
              id="showHeader"
              checked={settings.showHeader}
              onCheckedChange={(checked) =>
                updateSetting("showHeader", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showFooter">Mostrar rodapé</Label>
            <Switch
              id="showFooter"
              checked={settings.showFooter}
              onCheckedChange={(checked) =>
                updateSetting("showFooter", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showLogo">Mostrar logo</Label>
            <Switch
              id="showLogo"
              checked={settings.showLogo}
              onCheckedChange={(checked) => updateSetting("showLogo", checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showCompanyInfo">Informações da empresa</Label>
            <Switch
              id="showCompanyInfo"
              checked={settings.showCompanyInfo}
              onCheckedChange={(checked) =>
                updateSetting("showCompanyInfo", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showCustomerInfo">Informações do cliente</Label>
            <Switch
              id="showCustomerInfo"
              checked={settings.showCustomerInfo}
              onCheckedChange={(checked) =>
                updateSetting("showCustomerInfo", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showCustomerDocument">Documento do cliente</Label>
            <Switch
              id="showCustomerDocument"
              checked={settings.showCustomerDocument}
              onCheckedChange={(checked) =>
                updateSetting("showCustomerDocument", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showDeliveryInfo">Informações de entrega</Label>
            <Switch
              id="showDeliveryInfo"
              checked={settings.showDeliveryInfo}
              onCheckedChange={(checked) =>
                updateSetting("showDeliveryInfo", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showItemPrices">Mostrar preços</Label>
            <Switch
              id="showItemPrices"
              checked={settings.showItemPrices}
              onCheckedChange={(checked) =>
                updateSetting("showItemPrices", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showItemQuantities">Mostrar quantidades</Label>
            <Switch
              id="showItemQuantities"
              checked={settings.showItemQuantities}
              onCheckedChange={(checked) =>
                updateSetting("showItemQuantities", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showSubtotals">Mostrar subtotais</Label>
            <Switch
              id="showSubtotals"
              checked={settings.showSubtotals}
              onCheckedChange={(checked) =>
                updateSetting("showSubtotals", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showPaymentDetails">Detalhes de pagamento</Label>
            <Switch
              id="showPaymentDetails"
              checked={settings.showPaymentDetails}
              onCheckedChange={(checked) =>
                updateSetting("showPaymentDetails", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showDescription">Mostrar observações</Label>
            <Switch
              id="showDescription"
              checked={settings.showDescription}
              onCheckedChange={(checked) =>
                updateSetting("showDescription", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="compactMode">Modo compacto</Label>
            <Switch
              id="compactMode"
              checked={settings.compactMode}
              onCheckedChange={(checked) =>
                updateSetting("compactMode", checked)
              }
            />
          </div>
        </div>
      </div>
    );
  }

  // Renderizar apenas a pré-visualização
  if (previewOnly) {
    return (
      <div
        ref={printContentRef}
        className="p-4 bg-white border border-gray-200 rounded-md mx-auto receipt-preview-content"
        style={{
          width: `${settings.paperWidth}mm`,
          maxWidth: "100%",
          fontSize: `${settings.fontSize}px`,
        }}
      >
        {settings.showHeader && (
          <div className="text-center mb-4">
            {settings.showLogo && report.companyInfo?.logo && (
              <div className="mb-2">
                <img
                  src={report.companyInfo.logo || "/placeholder.svg"}
                  alt={report.companyInfo.name}
                  className="h-16 mx-auto object-contain"
                />
              </div>
            )}

            {settings.showCompanyInfo && report.companyInfo && (
              <div className="mb-2">
                <h2 className="text-xl font-bold">{report.companyInfo.name}</h2>
                {report.companyInfo.document && (
                  <p className="text-xs">{report.companyInfo.document}</p>
                )}
                {report.companyInfo.address && (
                  <p className="text-xs">{report.companyInfo.address}</p>
                )}
                {report.companyInfo.phone && (
                  <p className="text-xs">Tel: {report.companyInfo.phone}</p>
                )}
              </div>
            )}

            <h3 className="text-lg font-bold mb-1">COMPROVANTE DE PEDIDO</h3>
            {settings.showOrderNumber && (
              <p className="text-sm">
                Pedido #{report.orderNumber || report.id.substring(0, 8)}
              </p>
            )}
            <p className="text-sm">Data: {formatDate(report.createdAt)}</p>
            <hr className="my-2 border-t border-dashed border-gray-300" />
          </div>
        )}

        {settings.showCustomerInfo && report.customer && (
          <div className="mb-4">
            <h3 className="font-bold text-base mb-1">Cliente:</h3>
            <p className="text-sm">{report.customer.name}</p>
            {settings.showCustomerDocument && report.customer.document && (
              <p className="text-sm">Documento: {report.customer.document}</p>
            )}
            {report.customer.phone && (
              <p className="text-sm">Tel: {report.customer.phone}</p>
            )}
            <hr className="my-2 border-t border-dashed border-gray-300" />
          </div>
        )}

        {settings.showDeliveryInfo && report.delivery && (
          <div className="mb-4">
            <h3 className="font-bold text-base mb-1">Entrega:</h3>
            <p className="text-sm">
              Tipo:{" "}
              {report.delivery.type === "DELIVERY" ? "Entrega" : "Retirada"}
            </p>
            {report.delivery.scheduledFor && (
              <p className="text-sm">
                Agendado para: {formatDate(report.delivery.scheduledFor)}
              </p>
            )}
            {report.delivery.address && (
              <div className="text-sm">
                <p>
                  {report.delivery.address.line1},{" "}
                  {report.delivery.address.number}
                </p>
                {report.delivery.address.line2 && (
                  <p>Complemento: {report.delivery.address.line2}</p>
                )}
                <p>
                  {report.delivery.address.neighborhood},{" "}
                  {report.delivery.address.city}
                </p>
                {report.delivery.address.zipCode && (
                  <p>
                    CEP: {formatCep(report.delivery.address.zipCode)}
                  </p>
                )}
              </div>
            )}
            <hr className="my-2 border-t border-dashed border-gray-300" />
          </div>
        )}

        <div className="mb-4">
          <h3 className="font-bold text-base mb-2">Itens:</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1">Produto</th>
                {settings.showItemQuantities && (
                  <th className="text-center py-1">Qtd</th>
                )}
                {settings.showItemPrices && (
                  <>
                    <th className="text-right py-1">Valor Unit.</th>
                    <th className="text-right py-1">Total</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {report.items.map((item, index) => (
                <tr key={`${item.id}-${index}`} className="border-b">
                  <td className="py-1">{item.name}</td>
                  {settings.showItemQuantities && (
                    <td className="text-center py-1">{item.quantity}</td>
                  )}
                  {settings.showItemPrices && (
                    <>
                      <td className="text-right py-1">
                        {numberToMoney(item.unitPrice)}
                      </td>
                      <td className="text-right py-1">
                        {numberToMoney(item.totalPrice)}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {settings.showDescription && report.description && (
            <div className="mt-3">
              <p>
                <strong>Observações:</strong> {report.description}
              </p>
            </div>
          )}
        </div>

        {settings.showSubtotals && (
          <div className="mb-4">
            <h3 className="font-bold text-base mb-1">Resumo Financeiro:</h3>
            <div className="flex justify-between">
              <p>Subtotal:</p>
              <p>{numberToMoney(report.subtotal)}</p>
            </div>
            {!!(report.discount && report.discount > 0) && (
              <div className="flex justify-between">
                <p>Desconto:</p>
                <p>-{numberToMoney(report.discount)}</p>
              </div>
            )}
            {!!(report.additional && report.additional > 0) && (
              <div className="flex justify-between">
                <p>Adicional:</p>
                <p>+{numberToMoney(report.additional)}</p>
              </div>
            )}
            {!!(report.tax && report.tax > 0) && (
              <div className="flex justify-between">
                <p>Imposto:</p>
                <p>+{numberToMoney(report.tax)}</p>
              </div>
            )}
            {report.delivery.deliveryValue &&
              report.delivery.deliveryValue > 0 && (
                <div className="flex justify-between">
                  <p>Taxa de Entrega:</p>
                  <p>{numberToMoney(report.delivery.deliveryValue)}</p>
                </div>
              )}
            <div className="flex justify-between font-bold border-t pt-1 mt-1">
              <p>Total:</p>
              <p>{numberToMoney(report.totalAmount)}</p>
            </div>
          </div>
        )}

        {settings.showPaymentDetails &&
          report.payments &&
          report.payments.length > 0 && (
            <div className="mb-4">
              <h3 className="font-bold text-base mb-1">Pagamentos:</h3>
              {report.payments.map((payment, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span>
                    {payment.method} ({formatDate(payment.date)}):
                  </span>
                  <span>{numberToMoney(payment.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between mt-1">
                <p>Status do Pagamento:</p>
                <p>{report.paymentStatus}</p>
              </div>
            </div>
          )}

        {settings.showFooter && (
          <div className="text-center mt-4 text-sm">
            <hr className="my-2 border-t border-dashed border-gray-300" />
            <p>Obrigado pela preferência!</p>
            <p>
              Impresso em {new Date().toLocaleDateString("pt-BR")} às{" "}
              {new Date().toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {showControls && (
          <div className="flex justify-between items-center mb-4">
            <TabsList>
              <TabsTrigger value="preview" className="flex items-center gap-1">
                <Eye className="h-4 w-4" /> Visualizar
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-1">
                <Settings className="h-4 w-4" /> Configurações
              </TabsTrigger>
            </TabsList>

            <Button
              onClick={handlePrint}
              className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
            >
              <Printer className="h-4 w-4 mr-2" /> Imprimir
            </Button>
          </div>
        )}

        <TabsContent value="preview" className="mt-0">
          <Card className="border border-gray-200">
            <CardContent className="p-0">
              <div
                ref={printContentRef}
                className="p-4 bg-white"
                style={{
                  width: `${settings.paperWidth}mm`,
                  maxWidth: "100%",
                  margin: "0 auto",
                  fontSize: `${settings.fontSize}px`,
                }}
              >
                {settings.showHeader && (
                  <div className="text-center mb-4">
                    {settings.showLogo && report.companyInfo?.logo && (
                      <div className="mb-2">
                        <img
                          src={report.companyInfo.logo || "/placeholder.svg"}
                          alt={report.companyInfo.name}
                          className="h-16 mx-auto object-contain"
                        />
                      </div>
                    )}

                    {settings.showCompanyInfo && report.companyInfo && (
                      <div className="mb-2">
                        <h2 className="text-xl font-bold">
                          {report.companyInfo.name}
                        </h2>
                        {report.companyInfo.document && (
                          <p className="text-xs">
                            {report.companyInfo.document}
                          </p>
                        )}
                        {report.companyInfo.address && (
                          <p className="text-xs">
                            {report.companyInfo.address}
                          </p>
                        )}
                        {report.companyInfo.phone && (
                          <p className="text-xs">
                            Tel: {report.companyInfo.phone}
                          </p>
                        )}
                      </div>
                    )}

                    <h3 className="text-lg font-bold mb-1">
                      COMPROVANTE DE PEDIDO
                    </h3>
                    {settings.showOrderNumber && (
                      <p className="text-sm">
                        Pedido #
                        {report.orderNumber || report.id.substring(0, 8)}
                      </p>
                    )}
                    <p className="text-sm">
                      Data: {formatDate(report.createdAt)}
                    </p>
                    <hr className="my-2 border-t border-dashed border-gray-300" />
                  </div>
                )}

                {settings.showCustomerInfo && report.customer && (
                  <div className="mb-4">
                    <h3 className="font-bold text-base mb-1">Cliente:</h3>
                    <p className="text-sm">{report.customer.name}</p>
                    {settings.showCustomerDocument &&
                      report.customer.document && (
                        <p className="text-sm">
                          Documento: {report.customer.document}
                        </p>
                      )}
                    {report.customer.phone && (
                      <p className="text-sm">Tel: {report.customer.phone}</p>
                    )}
                    <hr className="my-2 border-t border-dashed border-gray-300" />
                  </div>
                )}

                {settings.showDeliveryInfo && report.delivery && (
                  <div className="mb-4">
                    <h3 className="font-bold text-base mb-1">Entrega:</h3>
                    <p className="text-sm">
                      Tipo:{" "}
                      {report.delivery.type === "DELIVERY"
                        ? "Entrega"
                        : "Retirada"}
                    </p>
                    {report.delivery.scheduledFor && (
                      <p className="text-sm">
                        Agendado para:{" "}
                        {formatDate(report.delivery.scheduledFor)}
                      </p>
                    )}
                    {report.delivery.address && (
                      <div className="text-sm">
                        <p>
                          {report.delivery.address.line1},{" "}
                          {report.delivery.address.number}
                        </p>
                        {report.delivery.address.line2 && (
                          <p>Complemento: {report.delivery.address.line2}</p>
                        )}
                        <p>
                          {report.delivery.address.neighborhood},{" "}
                          {report.delivery.address.city}
                        </p>
                        {report.delivery.address.zipCode && (
                          <p>
                            CEP: {formatCep(report.delivery.address.zipCode)}
                          </p>
                        )}
                      </div>
                    )}
                    <hr className="my-2 border-t border-dashed border-gray-300" />
                  </div>
                )}

                <div className="mb-4">
                  <h3 className="font-bold text-base mb-2">Itens:</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Produto</th>
                        {settings.showItemQuantities && (
                          <th className="text-center py-1">Qtd</th>
                        )}
                        {settings.showItemPrices && (
                          <>
                            <th className="text-right py-1">Valor Unit.</th>
                            <th className="text-right py-1">Total</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {report.items.map((item, index) => (
                        <>
                          <tr key={`${item.id}-${index}`} className="border-b">
                            <td className="py-1">
                              <div>
                                <span className="font-medium">{item.name}</span>
                              </div>
                            </td>
                            {settings.showItemQuantities && (
                              <td className="text-center py-1">
                                {item.quantity}
                              </td>
                            )}
                            {settings.showItemPrices && (
                              <>
                                <td className="text-right py-1">
                                  {numberToMoney(item.unitPrice)}
                                </td>
                                <td className="text-right py-1">
                                  {numberToMoney(item.totalPrice)}
                                </td>
                              </>
                            )}
                          </tr>

                          {/* Exibir adicionais se existirem e estiver habilitado */}
                          {settings.showAdditionals &&
                            item.additionals &&
                            item.additionals.length > 0 &&
                            item.additionals.map((additional, addIndex) => (
                              <tr
                                key={`${additional.itemId}-${addIndex}`}
                                className="border-b bg-orange-50"
                              >
                                <td className="py-1 pl-4">
                                  <div className="text-sm">
                                    <span className="text-orange-700">
                                      + {additional.label}
                                    </span>
                                  </div>
                                </td>
                                {settings.showItemQuantities && (
                                  <td className="text-center py-1 text-orange-600">
                                    {additional.quantity}
                                  </td>
                                )}
                                {settings.showItemPrices && (
                                  <>
                                    <td className="text-right py-1 text-orange-600">
                                      {numberToMoney(additional.unitPrice)}
                                    </td>
                                    <td className="text-right py-1 text-orange-600">
                                      {numberToMoney(
                                        additional.unitPrice *
                                          additional.quantity
                                      )}
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                        </>
                      ))}
                    </tbody>
                  </table>

                  {settings.showDescription && report.description && (
                    <div className="mt-3">
                      <p>
                        <strong>Observações:</strong> {report.description}
                      </p>
                    </div>
                  )}
                </div>

                {settings.showSubtotals && (
                  <div className="mb-4">
                    <h3 className="font-bold text-base mb-1">
                      Resumo Financeiro:
                    </h3>
                    <div className="flex justify-between">
                      <p>Subtotal:</p>
                      <p>{numberToMoney(report.subtotal)}</p>
                    </div>
                    {!!(report.discount && report.discount > 0) && (
                      <div className="flex justify-between">
                        <p>Desconto:</p>
                        <p>-{numberToMoney(report.discount)}</p>
                      </div>
                    )}
                    {!!(report.additional && report.additional > 0) && (
                      <div className="flex justify-between">
                        <p>Adicional:</p>
                        <p>+{numberToMoney(report.additional)}</p>
                      </div>
                    )}
                    {!!(report.tax && report.tax > 0) && (
                      <div className="flex justify-between">
                        <p>Imposto:</p>
                        <p>+{numberToMoney(report.tax)}</p>
                      </div>
                    )}
                    {report.delivery.deliveryValue &&
                      report.delivery.deliveryValue > 0 && (
                        <div className="flex justify-between">
                          <p>Taxa de Entrega:</p>
                          <p>{numberToMoney(report.delivery.deliveryValue)}</p>
                        </div>
                      )}
                    <div className="flex justify-between font-bold border-t pt-1 mt-1">
                      <p>Total:</p>
                      <p>{numberToMoney(report.totalAmount)}</p>
                    </div>
                  </div>
                )}

                {settings.showPaymentDetails &&
                  report.payments &&
                  report.payments.length > 0 && (
                    <div className="mb-4">
                      <h3 className="font-bold text-base mb-1">Pagamentos:</h3>
                      {report.payments.map((payment, index) => (
                        <div
                          key={index}
                          className="flex justify-between text-sm"
                        >
                          <span>
                            {payment.method} ({formatDate(payment.date)}):
                          </span>
                          <span>{numberToMoney(payment.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between mt-1">
                        <p>Status do Pagamento:</p>
                        <p>{report.paymentStatus}</p>
                      </div>
                    </div>
                  )}

                {settings.showFooter && (
                  <div className="text-center mt-4 text-sm">
                    <hr className="my-2 border-t border-dashed border-gray-300" />
                    <p>Obrigado pela preferência!</p>
                    <p>
                      Impresso em {new Date().toLocaleDateString("pt-BR")} às{" "}
                      {new Date().toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-0">
          <Card>
            <CardContent className="p-4">
              <div className="grid gap-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">Configurações de Impressão</h3>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resetSettings}
                        className="flex items-center gap-1"
                      >
                        <RotateCcw className="h-4 w-4" /> Resetar
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveSettings}
                        className="flex items-center gap-1 bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
                      >
                        <Save className="h-4 w-4" /> Salvar
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Largura do papel (mm)</Label>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[settings.paperWidth]}
                        min={50}
                        max={210}
                        step={5}
                        onValueChange={(value) =>
                          updateSetting("paperWidth", value[0])
                        }
                        className="flex-1"
                      />
                      <span className="w-12 text-right">
                        {settings.paperWidth}mm
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tamanho da fonte</Label>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[settings.fontSize]}
                        min={10}
                        max={24}
                        step={1}
                        onValueChange={(value) =>
                          updateSetting("fontSize", value[0])
                        }
                        className="flex-1"
                      />
                      <span className="w-12 text-right">
                        {settings.fontSize}px
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Número de cópias</Label>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[settings.copies]}
                        min={1}
                        max={5}
                        step={1}
                        onValueChange={(value) =>
                          updateSetting("copies", value[0])
                        }
                        className="flex-1"
                      />
                      <span className="w-12 text-right">{settings.copies}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="showHeader">Mostrar cabeçalho</Label>
                      <Switch
                        id="showHeader"
                        checked={settings.showHeader}
                        onCheckedChange={(checked) =>
                          updateSetting("showHeader", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showFooter">Mostrar rodapé</Label>
                      <Switch
                        id="showFooter"
                        checked={settings.showFooter}
                        onCheckedChange={(checked) =>
                          updateSetting("showFooter", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showLogo">Mostrar logo</Label>
                      <Switch
                        id="showLogo"
                        checked={settings.showLogo}
                        onCheckedChange={(checked) =>
                          updateSetting("showLogo", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showCompanyInfo">
                        Informações da empresa
                      </Label>
                      <Switch
                        id="showCompanyInfo"
                        checked={settings.showCompanyInfo}
                        onCheckedChange={(checked) =>
                          updateSetting("showCompanyInfo", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showCustomerInfo">
                        Informações do cliente
                      </Label>
                      <Switch
                        id="showCustomerInfo"
                        checked={settings.showCustomerInfo}
                        onCheckedChange={(checked) =>
                          updateSetting("showCustomerInfo", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showCustomerDocument">
                        Documento do cliente
                      </Label>
                      <Switch
                        id="showCustomerDocument"
                        checked={settings.showCustomerDocument}
                        onCheckedChange={(checked) =>
                          updateSetting("showCustomerDocument", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showDeliveryInfo">
                        Informações de entrega
                      </Label>
                      <Switch
                        id="showDeliveryInfo"
                        checked={settings.showDeliveryInfo}
                        onCheckedChange={(checked) =>
                          updateSetting("showDeliveryInfo", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showItemPrices">Mostrar preços</Label>
                      <Switch
                        id="showItemPrices"
                        checked={settings.showItemPrices}
                        onCheckedChange={(checked) =>
                          updateSetting("showItemPrices", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showItemQuantities">
                        Mostrar quantidades
                      </Label>
                      <Switch
                        id="showItemQuantities"
                        checked={settings.showItemQuantities}
                        onCheckedChange={(checked) =>
                          updateSetting("showItemQuantities", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showSubtotals">Mostrar subtotais</Label>
                      <Switch
                        id="showSubtotals"
                        checked={settings.showSubtotals}
                        onCheckedChange={(checked) =>
                          updateSetting("showSubtotals", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showPaymentDetails">
                        Detalhes de pagamento
                      </Label>
                      <Switch
                        id="showPaymentDetails"
                        checked={settings.showPaymentDetails}
                        onCheckedChange={(checked) =>
                          updateSetting("showPaymentDetails", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showDescription">
                        Mostrar observações
                      </Label>
                      <Switch
                        id="showDescription"
                        checked={settings.showDescription}
                        onCheckedChange={(checked) =>
                          updateSetting("showDescription", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="showAdditionals">
                        Mostrar adicionais
                      </Label>
                      <Switch
                        id="showAdditionals"
                        checked={settings.showAdditionals}
                        onCheckedChange={(checked) =>
                          updateSetting("showAdditionals", checked)
                        }
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="compactMode">Modo compacto</Label>
                      <Switch
                        id="compactMode"
                        checked={settings.compactMode}
                        onCheckedChange={(checked) =>
                          updateSetting("compactMode", checked)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
